import { EXCEL_OPERATE_CONNECTOR_ID, MCP_CONNECTOR_IDS } from './mcp-connectors.js';

/** Product-facing catalog of installable **extension app bundles**. An extension
 *  app bundle (e.g. the Windows-only 한컴/HWP bundle) is the unit that an operator
 *  enables and a user installs — it is *not* an MCP connector, even though its
 *  agent tools are delivered through one.
 *
 *  This catalog sits one layer above {@link MCP_CONNECTOR_CATALOG}: it carries
 *  the install/runtime metadata (platform, install mode) the connector catalog
 *  has no place for, and maps each extension to the connector id(s) that act as
 *  its execution channel. Enforcement is unchanged — the runtime still gates on
 *  `active_mcp_connectors` / `mcp__<connector>__*`. The catalog only lets the
 *  admin/desktop surfaces speak in "extension app bundle" terms while projecting to,
 *  and reading back from, the existing connector allowlist.
 *
 *  Invariant: every id in {@link LocalExtensionCatalogEntry.connectorIds} MUST
 *  exist in {@link MCP_CONNECTOR_IDS}. Drift would let an operator enable an
 *  extension whose tools never appear. A unit test pins this. */
export interface LocalExtensionCatalogEntry {
  /** Stable product id, e.g. `'hwp'`. Matches the desktop's `LocalExtensionId`. */
  id: string;
  /** Primary, user-facing label, e.g. `'한컴 한글 확장 번들 (HWP)'`. */
  label: string;
  /** OSes the extension can install/run on. */
  platforms: Array<'win32' | 'darwin' | 'linux'>;
  /** How the extension pack reaches the desktop. */
  installMode: 'on-demand' | 'bundled' | 'external';
  /** Execution-channel MCP connector id(s). All must be in MCP_CONNECTOR_IDS. */
  connectorIds: string[];
  /** Optional fine-grained feature flags (reserved; unused in the HWP MVP). */
  featureFlags?: string[];
}

export const LOCAL_EXTENSION_CATALOG: LocalExtensionCatalogEntry[] = [
  {
    id: 'hwp',
    label: '한컴 한글 확장 번들 (HWP)',
    platforms: ['win32'],
    installMode: 'on-demand',
    connectorIds: ['HWP_Operate']
  }
];

export const LOCAL_EXTENSION_IDS: string[] = LOCAL_EXTENSION_CATALOG.map(
  (extension) => extension.id
);

/** An extension app bundle that was shipped and then retired. It can no longer
 *  be installed, enabled, run, or published; the ids are kept so existing
 *  installs, stored allowlists, and old sessions are recognized and shut out
 *  instead of falling through to "plain MCP server" or "unknown id" handling.
 *  Only the safe removal of a leftover pack stays available. */
export interface RetiredLocalExtensionEntry {
  id: string;
  label: string;
  platforms: Array<'win32' | 'darwin' | 'linux'>;
  connectorIds: string[];
  /** Legacy server-config names that referred to the same built-in connector. */
  legacyServerNames: string[];
}

export const RETIRED_LOCAL_EXTENSIONS: RetiredLocalExtensionEntry[] = [
  {
    id: 'excel',
    label: 'Microsoft Excel 확장 번들 (Excel)',
    platforms: ['win32', 'darwin'],
    connectorIds: [EXCEL_OPERATE_CONNECTOR_ID],
    legacyServerNames: ['Excel Operate']
  }
];

export function retiredLocalExtensionById(id: string): RetiredLocalExtensionEntry | undefined {
  return RETIRED_LOCAL_EXTENSIONS.find((extension) => extension.id === id);
}

export function isRetiredLocalExtensionId(id: string): boolean {
  return retiredLocalExtensionById(id) !== undefined;
}

/** Whether a connector id (or a legacy server-config name for it) belongs to a
 *  retired extension app. Exact-string match, like every other connector gate. */
export function isRetiredConnectorId(connectorId: string): boolean {
  return RETIRED_LOCAL_EXTENSIONS.some(
    (extension) =>
      extension.connectorIds.includes(connectorId) ||
      extension.legacyServerNames.includes(connectorId)
  );
}

/** Drop retired connector ids from an allowlist/selection, keeping order. */
export function withoutRetiredConnectorIds(connectorIds: readonly string[]): string[] {
  return connectorIds.filter((connectorId) => !isRetiredConnectorId(connectorId));
}

/** Look up a single catalog entry by extension id. */
export function localExtensionById(id: string): LocalExtensionCatalogEntry | undefined {
  return LOCAL_EXTENSION_CATALOG.find((extension) => extension.id === id);
}

/** Whether an extension bundle supports the running desktop platform. */
export function supportsPlatform(entry: LocalExtensionCatalogEntry, platform: string): boolean {
  return entry.platforms.includes(platform as 'win32' | 'darwin' | 'linux');
}

/** Project a set of extension ids to the connector ids that back them. Unknown
 *  extension ids are ignored. Result is de-duplicated. This is the *write-side*
 *  mapping: extension selection -> `active_mcp_connectors`. */
export function connectorIdsForExtensions(extensionIds: readonly string[]): string[] {
  const connectorIds = new Set<string>();
  for (const extensionId of extensionIds) {
    const entry = localExtensionById(extensionId);
    if (!entry) continue;
    for (const connectorId of entry.connectorIds) connectorIds.add(connectorId);
  }
  return [...connectorIds];
}

/** Derive the set of *fully active* extension ids from a connector allowlist —
 *  an extension counts only when **all** of its backing connectors are present.
 *  This is the *read-side* mapping: `active_mcp_connectors` -> extension state. */
export function extensionsFromConnectorIds(connectorIds: readonly string[]): string[] {
  const active = new Set(connectorIds);
  return LOCAL_EXTENSION_CATALOG.filter((extension) =>
    extension.connectorIds.every((id) => active.has(id))
  ).map((extension) => extension.id);
}

/** Catalog entries whose backing connectors intersect the given connector ids.
 *  Used by the admin UI to decide which extensions to *offer* given a tenant's
 *  assignable connector set. */
export function localExtensionsForConnectorIds(
  connectorIds: readonly string[]
): LocalExtensionCatalogEntry[] {
  const available = new Set(connectorIds);
  return LOCAL_EXTENSION_CATALOG.filter((extension) =>
    extension.connectorIds.some((id) => available.has(id))
  );
}

/** Reverse of {@link connectorIdsForExtensions}: which extension app owns a
 *  connector id. Connectors outside the extension catalog return `undefined`,
 *  which is how callers tell "plain MCP server" from "extension channel". */
export function localExtensionForConnectorId(
  connectorId: string
): LocalExtensionCatalogEntry | undefined {
  return LOCAL_EXTENSION_CATALOG.find((extension) =>
    extension.connectorIds.includes(connectorId)
  );
}

/** Drop catalog connectors whose owning app is **explicitly** switched off.
 *
 *  Only an explicit `false` removes a connector: an id with no stored preference
 *  is a legacy install whose activation is resolved elsewhere from pack presence
 *  (decision D-2), and connectors that back no catalog extension are ordinary
 *  MCP servers this filter must never touch. An app with several backing
 *  connectors loses all of them at once. */
export function withoutDisabledExtensionConnectors(
  connectorIds: readonly string[],
  extensionAppEnabled: Readonly<Record<string, boolean>> | undefined
): string[] {
  if (!extensionAppEnabled) return [...connectorIds];
  return connectorIds.filter((connectorId) => {
    const owner = localExtensionForConnectorId(connectorId);
    if (!owner) return true;
    return extensionAppEnabled[owner.id] !== false;
  });
}
